import React from "react";
import styles from "./RatingStars.module.scss";
import {useTranslation} from "react-i18next";

type RatingStarsProps = {
  rating: number;
  size?: "small" | "medium" | "large";
  customSize?: number;
  color?: string;
  metaProp?: boolean;
};

const RatingStars: React.FC<RatingStarsProps> = ({
  rating,
  size = "medium",
  customSize,
  color = "#1ba1e2",
  metaProp = true,
}) => {
  const {t} = useTranslation();
  const ratingProcessed = Math.round(rating > 10 ? 10 : rating);
  const FilledStarsCount = (ratingProcessed - (ratingProcessed % 2)) / 2;
  const EmptyStarsCount = 5 - FilledStarsCount - (ratingProcessed % 2);
  const isHalfStar = ratingProcessed % 2 === 1;
  const ratingForTitle = new Intl.NumberFormat([], {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(
    rating / 2,
  );

  return (
    <div
      itemProp={metaProp ? "reviewRating" : undefined}
      itemType={metaProp ? "http://schema.org/Rating" : undefined}
      className={styles.wrapper}
      title={t("{{rating}} von 5 Sternen", {
        rating: ratingForTitle,
      })}
    >
      {metaProp && <meta itemProp="ratingValue" content={ratingProcessed.toString()} />}
      {Array.from({length: FilledStarsCount}, (_, index) => {
        const classConcat = [styles.starIcon, styles[size]].join(" ");

        return (
          <div key={`filled${index}`} style={customSize ? {width: customSize} : undefined} className={classConcat}>
            <FullStar color={color} />
          </div>
        );
      })}
      {isHalfStar && (
        <div style={customSize ? {width: customSize} : undefined} className={[styles.starIcon, styles[size]].join(" ")}>
          <HalfStar color={color} />
        </div>
      )}
      {Array.from({length: EmptyStarsCount}, (_, index) => {
        const classConcat = [styles.starIcon, styles[size]].join(" ");

        return (
          <div key={index} style={customSize ? {width: customSize} : undefined} className={classConcat}>
            <EmptyStar color={color} />
          </div>
        );
      })}
    </div>
  );
};

const strokeWidth = 3;

const HalfStar: React.FC<{color: string}> = ({color}) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Ebene_1" x="0" y="0" viewBox="0 0 48 48">
      <g stroke={color} strokeMiterlimit={10}>
        <path fill={color} strokeWidth={strokeWidth} d="M1.458 19.029l12.1 10.7-3.7 15.9 13.7-8.5v-34.5l-6.2 15z" />
        <path fill="none" strokeWidth={strokeWidth} d="M46.644 19.029l-15.9-1.4-6.3-15v34.5l13.8 8.5-3.7-15.9z" />
      </g>
    </svg>
  );
};

const EmptyStar: React.FC<{color: string}> = ({color}) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Ebene_1" x="0" y="0" viewBox="0 0 48 48">
      <path
        d="M24.1 37.1l14 8.4-3.7-15.9L46.7 19l-16.3-1.4-6.4-15-6.4 15L1.5 19l12.3 10.7-3.7 15.9 14-8.5z"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeMiterlimit={10}
      />
    </svg>
  );
};

const FullStar: React.FC<{color: string}> = ({color}) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Ebene_1" x="0" y="0" viewBox="0 0 48 48">
      <path
        d="M24.1 37.1l14 8.4-3.7-15.9L46.7 19l-16.3-1.4-6.4-15-6.4 15L1.5 19l12.3 10.7-3.7 15.9 14-8.5z"
        fill={color}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeMiterlimit={10}
      />
    </svg>
  );
};

export default RatingStars;
